import React from "react";
import { UserCheck, Users, Sparkles, Shield, MessageSquare, MapPin } from "lucide-react";

const features = [
  {
    icon: UserCheck,
    title: "Verified Travelers",
    description: "100% verified profiles for your safety",
  },
  {
    icon: Users,
    title: "Smart Matching",
    description: "Find travelers with similar interests & plans",
  },
  {
    icon: Sparkles,
    title: "AI Trip Assistant",
    description: "Get AI-powered travel recommendations",
  },
  {
    icon: Shield,
    title: "Safety First",
    description: "Advanced safety features for worry-free travel",
  },
  {
    icon: MessageSquare,
    title: "Instant Connect",
    description: "Chat instantly and plan your journey",
  },
  {
    icon: MapPin,
    title: "Nearby Travelers",
    description: "Discover travelers near you right now",
  },
];

const avatars = [
  { src: "https://images.unsplash.com/photo-1534528741775-53994a69daeb?auto=format&fit=crop&w=120&q=80", className: "top-[-10px] left-[18%]" },
  { src: "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?auto=format&fit=crop&w=120&q=80", className: "top-[20%] right-[-12px]" },
  { src: "https://images.unsplash.com/photo-1494790108377-be9c29b29330?auto=format&fit=crop&w=120&q=80", className: "bottom-[28%] left-[-16px]" },
  { src: "https://images.unsplash.com/photo-1500648767791-00dcc994a43e?auto=format&fit=crop&w=120&q=80", className: "bottom-[12%] right-[5%]" },
];

export default function MoreThanApp() {
  return (
    <section className="pt-12 pb-4 md:pt-16 md:pb-6 bg-[#F8F7FD]">
      <div className="max-w-7xl mx-auto px-6 grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-14 items-center">
        
        {/* LEFT SIDE: Image with Floating Connected Avatars */}
        <div className="lg:col-span-5 relative flex justify-center" data-aos="fade-right" data-aos-duration="1000">
          <div className="relative w-full max-w-[400px]">
            {/* Main Image Container */}
            <div className="w-full aspect-[4/3.8] rounded-[2.5rem] overflow-hidden shadow-2xl relative z-10 border-4 border-white">
              <img
                src="https://images.unsplash.com/photo-1501555088652-021faa106b9b?auto=format&fit=crop&w=800&q=80"
                alt="Solo traveler sitting on mountain top"
                className="w-full h-full object-cover"
              />
            </div>

            {/* Dotted Connection Lines (SVG Overlay) */}
            <svg className="absolute inset-0 w-full h-full z-20 pointer-events-none stroke-purple-400/60" style={{ filter: 'drop-shadow(0 2px 4px rgba(147, 51, 234, 0.3))' }}>
              <path d="M 90 20 Q 180 -10 280 40" fill="none" strokeWidth="2" strokeDasharray="4 4" />
              <path d="M 20 220 Q 50 140 100 120" fill="none" strokeWidth="2" strokeDasharray="4 4" />
              <path d="M 280 100 Q 320 180 290 260" fill="none" strokeWidth="2" strokeDasharray="4 4" />
            </svg>

            {/* Floating Avatar Circles */}
            {avatars.map((avatar, idx) => (
              <div
                key={idx}
                className={`absolute ${avatar.className} w-10 h-10 md:w-11 md:h-11 rounded-full p-0.5 bg-white shadow-xl z-30 transition-transform hover:scale-110`}
                data-aos="zoom-in"
                data-aos-delay={300 + idx * 150}
              >
                <img
                  src={avatar.src}
                  alt="Connected traveler"
                  className="w-full h-full object-cover rounded-full"
                />
              </div>
            ))}
          </div>
        </div>

        {/* RIGHT SIDE: Header, 2x3 Feature Grid & Action Button */}
        <div className="lg:col-span-7 space-y-6">
          
          {/* Header Section */}
          <div className="space-y-3" data-aos="fade-down" data-aos-duration="800">
            <span className="inline-block text-[11px] font-bold text-purple-600 tracking-wider bg-purple-100/80 px-4 py-1.5 rounded-full uppercase">
              WHY SOLOCONNECT?
            </span>
            <h2 className="text-3xl md:text-4xl font-extrabold text-slate-900 tracking-tight">
              More Than Just a <span className="text-[#8B2FC9]">Travel App</span>
            </h2>
          </div>

          {/* 2x3 Features Grid (Image Perfect Match) */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 pt-1">
            {features.map((feature, idx) => (
              <div
                key={feature.title}
                className="bg-white p-4 md:p-4.5 rounded-2xl shadow-sm hover:shadow-md border border-slate-100/80 transition-all flex items-start gap-3.5 group"
                data-aos="fade-up"
                data-aos-delay={idx * 100}
              >
                <div className="w-10 h-10 flex-shrink-0 rounded-full bg-purple-50 text-purple-600 flex items-center justify-center group-hover:bg-purple-600 group-hover:text-white transition-colors">
                  <feature.icon className="w-5 h-5 stroke-[2]" />
                </div>
                <div className="space-y-0.5">
                  <h3 className="font-bold text-xs md:text-sm text-slate-900 leading-snug">
                    {feature.title}
                  </h3>
                  <p className="text-[11px] md:text-xs text-slate-500 leading-relaxed">
                    {feature.description}
                  </p>
                </div>
              </div>
            ))}
          </div>

      

        </div>

      </div>
    </section>
  );
}